import React, { useMemo, useState } from 'react';
import {
  CccCalEvent,
  PT_WEEKDAY_SHORT,
  formatDateLong,
  formatTime,
  isSameDay,
  isSameMonth,
  monthGridDays,
  parseIsoLocal,
  toDateKey,
} from './cccCalTypes';

type MonthGridProps = {
  monthDate: Date;
  today: Date;
  events: CccCalEvent[];
  conflictIds?: Set<number>;
  onSelectEvent: (event: CccCalEvent, anchor: DOMRect) => void;
  onOpenDay: (date: Date) => void;
};

const MAX_CHIPS = 3;

const MonthGrid: React.FC<MonthGridProps> = ({
  monthDate,
  today,
  events,
  conflictIds,
  onSelectEvent,
  onOpenDay,
}) => {
  const days = useMemo(() => monthGridDays(monthDate), [monthDate]);
  const [dayPopover, setDayPopover] = useState<Date | null>(null);

  const eventsByDay = useMemo(() => {
    const map: Record<string, CccCalEvent[]> = {};
    events.forEach((ev) => {
      if (ev.status === 'cancelled') return;
      const key = toDateKey(parseIsoLocal(ev.starts_at));
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    Object.values(map).forEach((list) =>
      list.sort(
        (a, b) => parseIsoLocal(a.starts_at).getTime() - parseIsoLocal(b.starts_at).getTime(),
      ),
    );
    return map;
  }, [events]);

  return (
    <div className="grh-month">
      <div className="grh-month-weekdays">
        {PT_WEEKDAY_SHORT.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>

      <div className="grh-month-grid">
        {days.map((d) => {
          const key = toDateKey(d);
          const dayEvents = eventsByDay[key] || [];
          const isOther = !isSameMonth(d, monthDate);
          const isToday = isSameDay(d, today);
          const visible = dayEvents.slice(0, MAX_CHIPS);
          const extra = dayEvents.length - visible.length;

          return (
            <div
              key={key}
              className={[
                'grh-month-cell',
                isOther ? 'is-other' : '',
                isToday ? 'is-today' : '',
              ].filter(Boolean).join(' ')}
              onDoubleClick={() => onOpenDay(d)}
            >
              <div className="grh-month-cell-head">
                <button
                  type="button"
                  className={`grh-month-cell-num${isToday ? ' is-today' : ''}`}
                  onClick={() => onOpenDay(d)}
                  aria-label={`Ir para ${d.toDateString()}`}
                >
                  {d.getDate()}
                </button>
              </div>
              <div className="grh-month-cell-events">
                {visible.map((ev) => {
                  const start = parseIsoLocal(ev.starts_at);
                  const hasConflict = Boolean(conflictIds?.has(ev.id));
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      className={`grh-month-chip area-${ev.tone}${hasConflict ? ' is-conflict' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectEvent(ev, (e.currentTarget as HTMLElement).getBoundingClientRect());
                      }}
                      title={`${hasConflict ? '⚠️ Conflito · ' : ''}${formatTime(start)} · ${ev.title}`}
                    >
                      {hasConflict && <span className="grh-month-chip-conflict">⚠</span>}
                      <span className="grh-month-chip-time">{formatTime(start)}</span>
                      <span className="grh-month-chip-name">
                        {ev.title}
                        {ev.subtitle && (
                          <span className="grh-month-chip-phone"> ({ev.subtitle})</span>
                        )}
                      </span>
                    </button>
                  );
                })}
                {extra > 0 && (
                  <button
                    type="button"
                    className="grh-month-more"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDayPopover(d);
                    }}
                  >
                    +{extra} mais
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dayPopover && (
        <>
          <div
            className="grh-popover-scrim"
            role="presentation"
            onClick={() => setDayPopover(null)}
          />
          <div className="grh-month-day-popover" role="dialog" aria-modal="true">
            <div className="grh-month-day-popover-head">
              <span className="grh-month-day-popover-title">
                {formatDateLong(dayPopover)}
              </span>
              <button
                type="button"
                className="grh-popover-close"
                onClick={() => setDayPopover(null)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <div className="grh-month-day-popover-list">
              {(eventsByDay[toDateKey(dayPopover)] || []).map((ev) => {
                const start = parseIsoLocal(ev.starts_at);
                const end = parseIsoLocal(ev.ends_at);
                const hasConflict = Boolean(conflictIds?.has(ev.id));
                return (
                  <button
                    key={ev.id}
                    type="button"
                    className={`grh-month-day-popover-item area-${ev.tone}${hasConflict ? ' is-conflict' : ''}`}
                    onClick={(e) => {
                      setDayPopover(null);
                      onSelectEvent(ev, (e.currentTarget as HTMLElement).getBoundingClientRect());
                    }}
                  >
                    <span className="grh-month-day-popover-time">
                      {hasConflict ? '⚠ ' : ''}{formatTime(start)} – {formatTime(end)}
                    </span>
                    <span className="grh-month-day-popover-name">
                      {ev.title}
                      {ev.subtitle && (
                        <span className="grh-month-day-popover-phone"> ({ev.subtitle})</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="grh-month-day-popover-actions">
              <button
                type="button"
                className="grh-popover-btn is-primary"
                onClick={() => {
                  onOpenDay(dayPopover);
                  setDayPopover(null);
                }}
              >
                Ver dia
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthGrid;
